import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { functions, auth } from '@/boot/firebase';
import { isNoResponseCallableError } from '@/utils/callableErrors';
import {
  CallTimeoutError,
  callDeadlineFor,
  warmAuthToken,
  withDeadline,
  TOKEN_WARMUP_DEADLINE_MS,
} from '@/utils/callDeadline';

// Distinct error class for authentication failures
export class AuthError extends Error {
  constructor(message: string = 'Authentication required') {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Transient Cloud Function error codes — retried with exponential backoff.
 * These are infrastructure / availability issues, not logic errors:
 *   - `unavailable`: CF instance temporarily down (common during deployments)
 *   - `deadline-exceeded`: CF took longer than the timeout (transient overload)
 *   - `internal`: GCP-internal hiccup (rare but real)
 *   - `aborted`: transaction conflict (Firestore-side; usually re-runnable)
 *
 * NOT retried (genuine errors, not transient):
 *   - permission-denied, unauthenticated  (auth path — see below)
 *   - invalid-argument, failed-precondition, not-found, already-exists
 *   - resource-exhausted (quota/rate-limit; retrying makes it worse)
 *   - cancelled (user-initiated)
 */
const TRANSIENT_ERROR_CODES: ReadonlySet<string> = new Set([
  'functions/unavailable',
  'functions/deadline-exceeded',
  'functions/internal',
  'functions/aborted',
]);

/**
 * The codes of a failed call whose WRITE may still have landed — the reply was lost, not
 * refused (deep review M2 / R2, 2026-09-30): the transport gave up (`unavailable`,
 * `deadline-exceeded` — a client-side `CallTimeoutError` carries that code too) or the
 * transaction lost a race (`aborted`). NOT `internal`, although it is retried as transient
 * for reads: the backend answers `internal` for a definitive throw (an unexpected error
 * inside the function), so that write did not land. The one `internal` that IS a lost reply —
 * the SDK's bare 'internal' when no response arrived at all — is told by the message, not the
 * code: isLostReplyError reads the whole error.
 */
const LOST_REPLY_CODES: ReadonlySet<string> = new Set([
  'functions/unavailable',
  'functions/deadline-exceeded',
  'functions/aborted',
]);

/**
 * A failed call whose write may still have landed (LOST_REPLY_CODES). Every other code is
 * the backend's answer (a refusal): the write did not happen. A caller that must tell "done,
 * reply lost" from "refused" confirms with the live data too (e.g. the record left its list,
 * once the list has caught up) — this code alone never proves the write.
 */
export function isLostReply(code: string | undefined): boolean {
  return !!code && LOST_REPLY_CODES.has(code);
}

/**
 * 常態班更名 D3 (PO 2026-10-09 「ok fix it later」, T-S4-14) — isLostReply read off the whole
 * error: a lost-reply code, OR no response at all (the connection dropped — the SDK's bare
 * `functions/internal` 'internal', utils/callableErrors isNoResponseCallableError, the same
 * test that shows FUNC_CONNECTION_LOST). A server-sent `internal` (it always carries a message)
 * stays a refusal. Same caveat as isLostReply: this never proves the write landed.
 */
export function isLostReplyError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return (
    (typeof code === 'string' && isLostReply(code)) || isNoResponseCallableError(code, message)
  );
}

const RETRY_BACKOFFS_MS = [100, 300, 900] as const;

/**
 * Read vs. mutation retry convention (payment submit-safety, Wave 2).
 *
 * WHY: `callWithTransientRetry` re-issues the request on transient errors
 * (`deadline-exceeded` / `internal` / `aborted`). For a READ that is harmless —
 * worst case is a wasted round-trip. For a MUTATION it is dangerous: a Cloud
 * Function can time out on the CLIENT (deadline-exceeded) AFTER it has already
 * committed on the server, so a blind retry silently RE-EXECUTES the write
 * (double payment, double approval, etc.). During cold starts (5–15 s) this is
 * a real double-invoke path.
 *
 * DECISION (PO-approved): reads keep retrying; mutations must NOT auto-retry —
 * they fail loudly so the caller/user can decide. Callers may override per-call
 * via `callFunction(name, data, { retryTransient: true | false })` — explicit
 * always wins. When no override is given, the default is derived from the CF
 * name: a name whose namespace segment is a read verb
 * (`get` / `list` / `fetch` / `check` / `search` / `status`) immediately followed
 * by an uppercase letter or digit is treated as a READ and retries; everything
 * else is treated as a MUTATION and does NOT retry.
 *
 * Regex requires the read verb to sit RIGHT AFTER the `namespace-` dash so that
 * mutations like `payment-markScheduleCheck` (verb = `mark`) or
 * `payment-updateReceiptStatus` (verb = `update`) are correctly NOT matched.
 * The pattern is APP-WIDE by design: it matches every read-verb CF in every
 * module (payment-getPaymentRequests, scheduling-getRoster,
 * attendance-getMonthlySummary, employees-getEmployees, …), not just payment.
 * Audited against the full `callFunction(...)` name set on 2026-07-03: every
 * match is a genuine read; zero mutations match (`payment-markScheduleCheck`
 * verb = `mark`, `payment-updateReceiptStatus` verb = `update`). `verify*` is
 * intentionally excluded — those write (verification flags / attempt logs) and
 * should fail loudly. New CFs: name reads with a read verb right after the
 * dash, or pass an explicit `retryTransient` override at the call site.
 */
const READ_ONLY_FN_PATTERN = /-(get|list|fetch|check|search|status)[A-Z0-9]/;

function shouldRetryTransient(name: string, retryTransient?: boolean): boolean {
  if (typeof retryTransient === 'boolean') return retryTransient;
  return READ_ONLY_FN_PATTERN.test(name);
}

function isTransientError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  // A local deadline means the call stalled before it was ever sent (token
  // acquisition). Retrying stalls identically — fail loudly instead. Checked
  // BEFORE the code lookup because CallTimeoutError deliberately carries the
  // SDK's own `functions/deadline-exceeded` code for message-mapping purposes.
  if (error instanceof CallTimeoutError) return false;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && TRANSIENT_ERROR_CODES.has(code);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry helper for transient errors. Calls `attempt()` up to `RETRY_BACKOFFS_MS.length + 1`
 * times. On a transient error, sleeps the corresponding backoff then retries.
 * On any non-transient error (or after exhausting retries), throws.
 */
async function callWithTransientRetry<T>(attempt: () => Promise<T>, name: string): Promise<T> {
  let lastError: unknown;
  const maxAttempts = RETRY_BACKOFFS_MS.length + 1;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      return await attempt();
    } catch (error) {
      lastError = error;
      if (!isTransientError(error)) throw error;
      if (i < maxAttempts - 1) {
        const delay = RETRY_BACKOFFS_MS[i] ?? 0;
        if (import.meta.env.DEV) {
          const code = (error as { code?: string }).code;
          console.warn(
            `[useFirebaseFunctions] Transient ${code} on "${name}" (attempt ${i + 1}/${maxAttempts}); retry in ${delay}ms`,
          );
        }
        await sleep(delay);
      }
    }
  }
  throw lastError;
}

/** Per-call options for {@link useFirebaseFunctions.callFunction}. */
export interface CallFunctionOptions {
  /**
   * Override the read/mutation transient-retry default (see `READ_ONLY_FN_PATTERN`).
   * `true` = always retry transient errors, `false` = never. Omit to auto-decide
   * by CF name (reads retry, mutations do not).
   */
  retryTransient?: boolean;
  /**
   * Per-call client-side deadline in MILLISECONDS. Omit to keep the Firebase SDK
   * default (70 s) — which is right for every ordinary callable. Set it only for a
   * CF whose own `timeoutSeconds` is deliberately longer (e.g. the 差旅報告單 bundle,
   * 540 s): without it the browser aborts with `deadline-exceeded` while the server
   * is still legitimately working, and the user sees a failure for a job that
   * succeeded. Mirror the CF's `timeoutSeconds` × 1000.
   */
  timeoutMs?: number;
}

export function useFirebaseFunctions() {
  const callFunction = async <T = any>(
    name: string,
    data: any,
    opts?: CallFunctionOptions,
  ): Promise<T> => {
    // Options are passed ONLY when a caller asked for them, so every other call
    // keeps the SDK's own defaults (70 s timeout) untouched.
    const callable: HttpsCallable =
      opts?.timeoutMs === undefined
        ? httpsCallable(functions, name)
        : httpsCallable(functions, name, { timeout: opts.timeoutMs });
    // Backstop only — sized so it can never fire before the SDK's own timer.
    // See src/utils/callDeadline.ts for why both layers exist.
    const deadlineMs = callDeadlineFor(opts?.timeoutMs);
    const attempt = async (): Promise<T> => {
      // Bound the auth-token phase FIRST. Once it resolves, the SDK's own
      // makeAuthHeaders() reads it from cache and arms its timer immediately,
      // so the network phase is covered by the SDK as designed.
      await warmAuthToken(name);
      const result = await withDeadline(callable(data), deadlineMs, name);
      return result.data as T;
    };
    const retryTransient = shouldRetryTransient(name, opts?.retryTransient);

    try {
      // Reads retry on transient infrastructure errors (deploys / overload / aborted txns);
      // mutations run once so a timed-out-but-committed write is not silently re-executed.
      return await (retryTransient ? callWithTransientRetry(attempt, name) : attempt());
    } catch (error: any) {
      // Then: special-case unauthenticated → refresh token + retry once.
      if (
        error?.code === 'functions/unauthenticated' ||
        error?.message?.includes('unauthenticated')
      ) {
        console.warn(
          `[useFirebaseFunctions] Auth error calling ${name}. Attempting token refresh...`,
        );

        try {
          if (auth.currentUser) {
            // Deadline-guarded for the same reason as the call itself: a FORCED
            // token refresh on a dead network can hang with no timer of its own.
            await withDeadline(
              auth.currentUser.getIdToken(true),
              TOKEN_WARMUP_DEADLINE_MS,
              `${name} (token refresh)`,
            );
            console.log('[useFirebaseFunctions] Token refreshed successfully. Retrying call...');
            // After token refresh, retry once. We do NOT re-apply transient-retry here
            // because at this point we've already exhausted those attempts.
            const result = await withDeadline(callable(data), deadlineMs, name);
            return result.data as T;
          } else {
            console.warn('[useFirebaseFunctions] No current user to refresh token.');
            throw new AuthError('User not logged in');
          }
        } catch (retryError) {
          console.error(`[useFirebaseFunctions] Retry failed for ${name}:`, retryError);
          // A stall is NOT an expired session. Rewriting it as AuthError strips
          // the `functions/` code, so parseError falls through to the generic
          // UNKNOWN_ERROR toast and the user is told to log in again for what is
          // actually a hung network — the exact scenario this deadline exists to
          // report honestly. Let it through unchanged.
          if (retryError instanceof CallTimeoutError) throw retryError;
          throw new AuthError('Session expired');
        }
      }

      throw error;
    }
  };

  return {
    callFunction,
  };
}
